import React, { InputHTMLAttributes } from "react";
import { FormIcon } from "@core/components/forms/FormIcon";
import { Input } from "@core/components/ui/Input";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { cn } from "@core/lib/utils";
import { FieldErrors } from "react-hook-form";
import { ErrorMessage } from "@core/components/forms/ErrorMessage";
import { useTheme } from "@core/hooks/useTheme";

export type LabeledInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string | React.ReactNode;
  icon?: IconDefinition;
  iconClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  errors?: FieldErrors<any>;
};

/**
 * Labeled Inputs are inputs with a `<label>` and support for an icon.
 * @example
 * ```tsx
 * <LabeledInput type="text" name="username" label="Username" icon={faUser} />
 * ```
 * They also support children, which are either displayed below the input or can be absolute:
 * ```tsx
 * <LabeledInput {...}>
 *   <div className="absolute p-2 right-0">
 *     <FontAwesomeIcon icon={faEye} />
 *   </div>
 * </LabeledInput>
 * ```
 */
export const LabeledInput = React.forwardRef<
  HTMLInputElement, LabeledInputProps
>(({ label, children, className, labelClassName, inputClassName, icon, iconClassName, errors, id, name, ...props }, ref) => {
  const c = useTheme("Form");
  const generatedId = React.useId();
  const inputId = id ?? name ?? generatedId;
  const roundedClass = icon
      ? "rounded-e-var[(--form-radius)] rounded-s-none"
      : "rounded-[var(--form-radius)]";
  return (
    <div className={className}>
      {label && (
        <label {...c("Label", "inline-block dark:text-white")} className={cn(c("Label", "").className, labelClassName)}
          htmlFor={inputId}>
          {label}{props.required && <span className="text-danger-500">&nbsp;*</span>}
        </label>
      )}
      <div className="flex relative">
        {icon && <FormIcon icon={icon} {...c("Icon", "")} className={cn(c("Icon", "").className, iconClassName)} />}
        <Input
          {...c(props.type === "checkbox" ? "CheckboxInput" : "TextInput", "peer dark:text-white")}
          className={cn(
            c(props.type === "checkbox" ? "CheckboxInput" : "TextInput", "").className,
            roundedClass,
            inputClassName
          )}
          id={inputId}
          name={name}
          {...props}
          ref={ref} />
        {children}
      </div>
      {name != null && errors != null && (
        <ErrorMessage errors={errors} name={name} />
      )}
    </div>
  );
});
LabeledInput.displayName = "LabeledInput";
